import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ExchangeRateService, RateSource } from '../../../features/exchange-rate/exchange-rate.service';
import { AccountType, GroupNumber } from '../../../domain/account.domain';
import { RestService } from '../../../services/rest/rest.service';
import { AccountsAndTotals } from '../../../domain/balances.domain';
import { Currency } from '../../../domain/market.domain';
import {
  BalancesSummary,
  BalancesSummaryResponse,
  ConsolidatedBalance,
  ConsolidatedBalanceResponse,
  TypeSummary
} from './balances-summary.domain';

@Injectable()
export class BalancesSummaryRestService {
  private readonly exchangeRateService = inject(ExchangeRateService);
  private readonly restService = inject(RestService);

  getBalancesSummary(): Observable<BalancesSummary> {
    const parameters = {
      aggregationType: [
        'PORTFOLIO_INVESTMENT_TYPE_TOTAL',
        'ACCOUNT_DATA_GROUPED_BY_NUMBER_TYPE'
      ]
    };

    return this.restService.getResource('portfolio_v2.aggregatedBalances')
      .get<BalancesSummaryResponse>({ params: parameters })
      .pipe(
        map((response: BalancesSummaryResponse): BalancesSummary => {
          if (response !== undefined) {
            return this.mapData(response);
          }
        })
      );
  }

  private mapData(data: BalancesSummaryResponse): BalancesSummary {
    const accountDetails:  Map<GroupNumber, Map<AccountType, AccountsAndTotals>> = data.ACCOUNT_DATA_GROUPED_BY_NUMBER_TYPE.result;
    const typeSummaries: Array<TypeSummary> = [];

    for (const groupNumber in accountDetails) {
      if (Object.getOwnPropertyDescriptor(accountDetails, groupNumber)) {
        const groupInfo: Map<AccountType, AccountsAndTotals> = accountDetails[groupNumber];

        for (const accountType in groupInfo) {
          if (Object.getOwnPropertyDescriptor(groupInfo, accountType)) {
            const accountsAndTotals: AccountsAndTotals = groupInfo[accountType];
            const typeSummary: TypeSummary = Object.assign({}, accountsAndTotals.accounts[0], accountsAndTotals.totals) as TypeSummary;
            typeSummaries.push(typeSummary);
          }
        }
      }
    }

    const balancesDate = typeSummaries[0].balanceDate;
    const ibmUsdToCadRate: number = this.exchangeRateService.getExchangeRate(Currency.USD, Currency.CAD, RateSource.IBM);
    const cbd: ConsolidatedBalanceResponse = data.PORTFOLIO_INVESTMENT_TYPE_TOTAL.result;
    const consolidatedBalance = new ConsolidatedBalance(balancesDate, ibmUsdToCadRate, cbd.totalCashBalance, cbd.totalSecurityBalance,
      cbd.totalBalance);

    const transformed = {
      consolidatedBalance,
      typeSummaries
    };
    return transformed;
  }
}

